import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Container,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Layout from '../../components/layout/Layout';
import gstReportService from '../../services/gstReportService';
import companySettingsService from '../../services/companySettingsService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import * as XLSX from 'xlsx-js-style';

/**
 * Borders and alignment for the GSTR-1 Summary block (no background colors).
 * Row indices are 0-based (title row 5 = Excel row 6; data rows 7–11 = Excel rows 8–12).
 */
const applyGstr1SummaryTableStyles = (ws) => {
  const borderBox = {
    top: { style: 'thin', color: { rgb: 'FF000000' } },
    bottom: { style: 'thin', color: { rgb: 'FF000000' } },
    left: { style: 'thin', color: { rgb: 'FF000000' } },
    right: { style: 'thin', color: { rgb: 'FF000000' } },
  };
  const white = { patternType: 'solid', fgColor: { rgb: 'FFFFFFFF' } };

  const SUMMARY_TITLE_ROW = 5;
  const HEADER_ROW = 6;
  const DATA_START = 7;
  const DATA_END = 11;
  const LAST_COL = 5;

  ws['!merges'] = [{ s: { r: SUMMARY_TITLE_ROW, c: 0 }, e: { r: SUMMARY_TITLE_ROW, c: LAST_COL } }];

  const setStyle = (r, c, style) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!ws[addr]) {
      ws[addr] = { t: 's', v: '' };
    }
    ws[addr].s = style;
  };

  for (let c = 0; c <= LAST_COL; c++) {
    setStyle(SUMMARY_TITLE_ROW, c, {
      font: { bold: true, sz: 12, color: { rgb: 'FF000000' } },
      fill: white,
      alignment: { horizontal: 'center', vertical: 'center' },
      border: borderBox,
    });
  }

  for (let c = 0; c <= LAST_COL; c++) {
    const isDesc = c === 0;
    setStyle(HEADER_ROW, c, {
      font: { bold: true, color: { rgb: 'FF000000' } },
      fill: white,
      alignment: {
        horizontal: isDesc ? 'left' : 'center',
        vertical: 'center',
        wrapText: true,
      },
      border: borderBox,
    });
  }

  for (let r = DATA_START; r <= DATA_END; r++) {
    for (let c = 0; c <= LAST_COL; c++) {
      const isDesc = c === 0;
      const cellStyle = {
        font: { bold: false, color: { rgb: 'FF000000' } },
        fill: white,
        alignment: {
          horizontal: isDesc ? 'left' : 'right',
          vertical: 'center',
        },
        border: borderBox,
      };
      if (!isDesc) {
        cellStyle.numFmt = '0.00';
      }
      setStyle(r, c, cellStyle);
    }
  }

  ws['!cols'] = [
    { wch: 42 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
  ];
};

/**
 * GSTR-1 HSN (B2B / B2C) Excel: title, column headers, data rows, blank row, then summary totals for visibility.
 */
const buildGstr1HsnSheetAoa = (title, summary) => {
  const rows = (summary && summary.rows) || [];
  const header = [
    'HSN',
    'Description',
    'UQC',
    'Total Quantity',
    'Total Value',
    'Rate',
    'Taxable Value',
    'IGST',
    'CGST',
    'SGST',
    'Cess',
  ];
  const dataRows = rows.map((r) => [
    r.hsn || '',
    r.description || '',
    r.uqc || '',
    Number(r.totalQuantity ?? 0),
    Number(r.totalValue ?? 0),
    Number(r.rate ?? 0),
    Number(r.taxableValue ?? 0),
    Number(r.igst ?? 0),
    Number(r.cgst ?? 0),
    Number(r.sgst ?? 0),
    Number(r.cess ?? 0),
  ]);
  return [
    [title],
    header,
    ...dataRows,
    [],
    [
      'No. of HSN',
      Number(summary?.numberOfHsn ?? 0),
      '',
      'Total Value',
      Number(summary?.totalValue ?? 0),
      '',
      'Total Taxable Val',
      Number(summary?.totalTaxableValue ?? 0),
      '',
      '',
      '',
    ],
  ];
};

const GSTReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    rows: [],
    summary: null,
    taxRates: [],
    gstr1B2bLines: [],
    gstr1B2bSummary: null,
    gstr1B2clLines: [],
    gstr1CdnrLines: [],
    gstr1CdnurLines: [],
    gstr1ExempSummary: null,
    gstr1HsnB2bSummary: null,
    gstr1HsnB2cSummary: null,
  });
  const [nonGstMode, setNonGstMode] = useState(false);

  // Default to current month
  const getCurrentMonthDates = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date();
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const [startDate, setStartDate] = useState(getCurrentMonthDates().startDate);
  const [endDate, setEndDate] = useState(getCurrentMonthDates().endDate);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (e) {
      return dateString;
    }
  };

  // Fetch report data
  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await gstReportService.getGSTReport(startDate, endDate, nonGstMode);
      setReportData(response);
    } catch (error) {
      console.error('Error fetching GST report:', error);
      toast.error('Failed to load GSTR1 report');
    } finally {
      setLoading(false);
    }
  };

  // Load report on mount and when dates change
  useEffect(() => {
    fetchReportData();
  }, [startDate, endDate, nonGstMode]);

  // Ctrl+Q toggles GST vs Non-GST view (entire page)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = String(e.key || '').toLowerCase();
      if (e.ctrlKey && key === 'q') {
        e.preventDefault();
        setNonGstMode((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  /** GSTR-1 EXEMP: any non-zero taxable value in nil / exempt / non-GST summary. */
  const exempSummaryHasData = (s) => {
    if (!s) return false;
    const t = (v) => Number(v || 0);
    return t(s.totalNilRatedSupplies) + t(s.totalExemptedSupplies) + t(s.totalNonGstSupplies) > 0;
  };

  /** Period text for Excel header (matches “Period | …” style). */
  const formatExcelReportPeriod = (start, end) => {
    const s = new Date(`${start}T12:00:00`);
    const e = new Date(`${end}T12:00:00`);
    return `${format(s, 'dd MMM yyyy')} – ${format(e, 'dd MMM yyyy')}`;
  };

  const isRegisteredB2bRow = (row) => {
    const g = row.gstNo;
    return g != null && String(g).trim() !== '';
  };

  /** GSTR-1 B2CL threshold (₹2.5 lakh); B2CS is at or below this. */
  const B2C_LAKH_THRESHOLD = 250000;

  const invoiceTotalValue = (row) => Number(row.totalAmount || 0);

  /** Inter-state supply: IGST charged on the invoice (tax name starts with IGST, amount > 0). */
  const isInterstateRow = (row) => {
    if (!row.taxAmounts) return false;
    return Object.entries(row.taxAmounts).some(
      ([name, v]) => name.trim().toUpperCase().startsWith('IGST') && Number(v) > 0
    );
  };

  /** First numeric rate in tax name, e.g. "GST 5%" / "IGST 18%" → 5 / 18 */
  const parseGstRatePercentFromTaxName = (taxName) => {
    const m = String(taxName).match(/(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  };

  /**
   * B2CS: group small B2C lines by place of supply (state) and GST rate — taxable ≈ taxAmount * 100 / rate.
   */
  const aggregateB2csTaxableByPlaceAndRate = (rows) => {
    const byKey = {};
    rows.forEach((row) => {
      if (!row.taxAmounts) return;
      const pos = (row.placeOfSupply && String(row.placeOfSupply).trim()) || '';
      Object.entries(row.taxAmounts).forEach(([taxName, amt]) => {
        const taxAmt = Number(amt);
        if (!taxAmt || taxAmt <= 0) return;
        const rate = parseGstRatePercentFromTaxName(taxName);
        if (!rate || rate <= 0) return;
        const taxable = (taxAmt * 100) / rate;
        const key = `${pos}|||${rate}`;
        if (!byKey[key]) {
          byKey[key] = { placeOfSupply: pos, rate: String(rate), taxableValue: 0, cessAmount: 0 };
        }
        byKey[key].taxableValue += taxable;
      });
    });
    return Object.values(byKey).sort((a, b) => {
      const c = String(a.placeOfSupply).localeCompare(String(b.placeOfSupply));
      if (c !== 0) return c;
      return Number(a.rate) - Number(b.rate);
    });
  };

  const aggregateRowsSummary = (rows, taxRates) => {
    let totalTaxableAmount = 0;
    let totalGST = 0;
    let totalAmount = 0;
    const totalTaxAmounts = {};
    taxRates.forEach((t) => {
      totalTaxAmounts[t.name] = 0;
    });
    rows.forEach((row) => {
      totalTaxableAmount += Number(row.taxableAmount || 0);
      totalGST += Number(row.totalGST || 0);
      totalAmount += Number(row.totalAmount || 0);
      taxRates.forEach((t) => {
        const v = row.taxAmounts && row.taxAmounts[t.name] ? row.taxAmounts[t.name] : 0;
        totalTaxAmounts[t.name] = (totalTaxAmounts[t.name] || 0) + Number(v);
      });
    });
    return { totalTaxableAmount, totalGST, totalAmount, totalTaxAmounts };
  };

  /** Totals for GSTR-1 summary sheet from backend B2B lines (CGST/SGST/IGST split). */
  const sumGstr1TotalsFromB2bLines = (lines) => {
    const z = { taxable: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
    (lines || []).forEach((line) => {
      z.taxable += Number(line.taxableValue || 0);
      z.igst += Number(line.igstAmount || 0);
      z.cgst += Number(line.cgstAmount || 0);
      z.sgst += Number(line.sgstAmount || 0);
      z.cess += Number(line.cessAmount || 0);
    });
    return z;
  };

  /** B2CL is IGST-only; CGST/SGST stay 0 on main summary. */
  const sumGstr1TotalsFromB2clLines = (lines) => {
    const taxable = (lines || []).reduce((a, l) => a + Number(l.taxableValue || 0), 0);
    const igst = (lines || []).reduce((a, l) => a + Number(l.igstAmount || 0), 0);
    const cess = (lines || []).reduce((a, l) => a + Number(l.cessAmount || 0), 0);
    return { taxable, igst, cgst: 0, sgst: 0, cess };
  };

  /** CDNR — same tax columns as B2B (CGST/SGST/IGST split from backend lines). */
  const sumGstr1TotalsFromCdnrLines = (lines) => {
    const z = { taxable: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
    (lines || []).forEach((line) => {
      z.taxable += Number(line.taxableValue || 0);
      z.igst += Number(line.igstAmount || 0);
      z.cgst += Number(line.cgstAmount || 0);
      z.sgst += Number(line.sgstAmount || 0);
      z.cess += Number(line.cessAmount || 0);
    });
    return z;
  };

  /** CDNUR — IGST (and cess) only on main summary. */
  const sumGstr1TotalsFromCdnurLines = (lines) => {
    const z = { taxable: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
    (lines || []).forEach((line) => {
      z.taxable += Number(line.taxableValue || 0);
      z.igst += Number(line.igstAmount || 0);
      z.cess += Number(line.cessAmount || 0);
    });
    return z;
  };

  /** IGST vs CGST+SGST split from invoice-level tax map (matches backend 50/50 for intra-state). */
  const sumGstr1TotalsFromInvoiceRows = (rows) => {
    let taxable = 0;
    let igst = 0;
    let cgst = 0;
    let sgst = 0;
    let cess = 0;
    (rows || []).forEach((row) => {
      taxable += Number(row.taxableAmount || 0);
      if (!row.taxAmounts) return;
      Object.entries(row.taxAmounts).forEach(([name, v]) => {
        const amt = Number(v);
        if (!amt || amt <= 0) return;
        const u = name.trim().toUpperCase();
        if (u.startsWith('IGST')) {
          igst += amt;
        } else {
          const half = Math.round((amt / 2) * 100) / 100;
          cgst += half;
          sgst += Math.round((amt - half) * 100) / 100;
        }
      });
    });
    return { taxable, igst, cgst, sgst, cess };
  };

  // Export to Excel — GSTR1 Report sheet: header + B2B/B2CL/B2CS summary table; EXEMP + detail sheets
  const handleExport = async () => {
    try {
      setIsExporting(true);

      let companySettings = {};
      try {
        companySettings = await companySettingsService.getSettings();
      } catch (e) {
        console.warn('Company settings not loaded for Excel header', e);
      }

      const companyName = (companySettings.name || '').trim();
      const companyGst = (companySettings.gst || companySettings.uen || '').trim();
      const periodLabel = formatExcelReportPeriod(startDate, endDate);

      const gstr1B2bLines = reportData.gstr1B2bLines || [];
      const gstr1B2clLines = reportData.gstr1B2clLines || [];
      const gstr1CdnrLinesForSummary = reportData.gstr1CdnrLines || [];
      const gstr1CdnurLinesForSummary = reportData.gstr1CdnurLines || [];
      const b2bSummaryTotals = sumGstr1TotalsFromB2bLines(gstr1B2bLines);
      const b2clSummaryTotals = sumGstr1TotalsFromB2clLines(gstr1B2clLines);
      const b2csRows = reportData.rows.filter(
        (r) => !isRegisteredB2bRow(r) && invoiceTotalValue(r) <= B2C_LAKH_THRESHOLD
      );
      const b2csSummaryTotals = sumGstr1TotalsFromInvoiceRows(b2csRows);
      const cdnrSummaryTotals = sumGstr1TotalsFromCdnrLines(gstr1CdnrLinesForSummary);
      const cdnurSummaryTotals = sumGstr1TotalsFromCdnurLines(gstr1CdnurLinesForSummary);

      const ws = XLSX.utils.aoa_to_sheet([
        ['Period', periodLabel],
        [],
        ['1. GSTIN', companyGst],
        ['2. Legal registered name', companyName],
        [],
        ['GSTR-1 Summary'],
        ['Description', 'Taxable Value', 'IGST', 'CGST', 'SGST', 'Cess'],
        [
          'B2B Invoices - 4A, 4B, 6B, 6C',
          b2bSummaryTotals.taxable,
          b2bSummaryTotals.igst,
          b2bSummaryTotals.cgst,
          b2bSummaryTotals.sgst,
          b2bSummaryTotals.cess,
        ],
        [
          'B2C Invoices - Large (5A, 5B) - B2CL',
          b2clSummaryTotals.taxable,
          b2clSummaryTotals.igst,
          b2clSummaryTotals.cgst,
          b2clSummaryTotals.sgst,
          b2clSummaryTotals.cess,
        ],
        [
          'B2C Invoices - Others (7) - B2CS',
          b2csSummaryTotals.taxable,
          b2csSummaryTotals.igst,
          b2csSummaryTotals.cgst,
          b2csSummaryTotals.sgst,
          b2csSummaryTotals.cess,
        ],
        [
          'Credit / Debit Notes (Registered) - CDNR (9B)',
          cdnrSummaryTotals.taxable,
          cdnrSummaryTotals.igst,
          cdnrSummaryTotals.cgst,
          cdnrSummaryTotals.sgst,
          cdnrSummaryTotals.cess,
        ],
        [
          'Credit / Debit Notes (Unregistered) - CDNUR (9B)',
          cdnurSummaryTotals.taxable,
          cdnurSummaryTotals.igst,
          cdnurSummaryTotals.cgst,
          cdnurSummaryTotals.sgst,
          cdnurSummaryTotals.cess,
        ],
      ]);
      applyGstr1SummaryTableStyles(ws);
      const gstr1B2bSummary = reportData.gstr1B2bSummary || {};

      const b2bExportRows = gstr1B2bLines.map((line) => ({
        'GSTIN/UIN of Recipient': line.gstinUinOfRecipient || '',
        'Receiver Name': line.receiverName || '',
        'Invoice Number': line.invoiceNumber || '',
        'Invoice Date': formatDate(line.invoiceDate),
        'Invoice Value': Number(line.invoiceValue || 0),
        'Place Of Supply': line.placeOfSupply || '',
        'Reverse Charge': line.reverseCharge || 'N',
        'Invoice Type': line.invoiceType || 'Regular',
        'E-Commerce GSTIN': line.ecommerceGstin || '',
        Rate: Number(line.rate ?? 0),
        'Taxable Value': Number(line.taxableValue || 0),
        'CGST Amount': Number(line.cgstAmount || 0),
        'SGST Amount': Number(line.sgstAmount || 0),
        'IGST Amount': Number(line.igstAmount || 0),
        'Cess Amount': Number(line.cessAmount || 0),
      }));

      const wsB2b = XLSX.utils.aoa_to_sheet([
        ['Summary For B2B, SEZ, DE (4A, 4B, 6B, 6C)'],
        [
          'No. of Recipients',
          'No. of Invoices',
          'Total Invoice Value',
          'Total Taxable Value',
          'Total Cess',
        ],
        [
          gstr1B2bSummary.numberOfRecipients ?? 0,
          gstr1B2bSummary.numberOfInvoices ?? 0,
          gstr1B2bSummary.totalInvoiceValue ?? 0,
          gstr1B2bSummary.totalTaxableValue ?? 0,
          gstr1B2bSummary.totalCess ?? 0,
        ],
        [],
      ]);

      const B2B_TABLE_ORIGIN_ROW = 5;

      if (b2bExportRows.length === 0) {
        XLSX.utils.sheet_add_aoa(
          wsB2b,
          [['No B2B lines in this period (no registered-GST sales with tax lines).']],
          { origin: `A${B2B_TABLE_ORIGIN_ROW}` }
        );
      } else {
        XLSX.utils.sheet_add_json(wsB2b, b2bExportRows, { origin: `A${B2B_TABLE_ORIGIN_ROW}` });
      }

      const b2clDistinctInvoices = new Set(
        (gstr1B2clLines || []).map((l) => l.invoiceNumber).filter(Boolean)
      ).size;
      let b2clTotalInvoiceValue = 0;
      const b2clSeenInv = new Set();
      (gstr1B2clLines || []).forEach((l) => {
        const inv = l.invoiceNumber;
        if (inv && !b2clSeenInv.has(inv)) {
          b2clSeenInv.add(inv);
          b2clTotalInvoiceValue += Number(l.invoiceValue || 0);
        }
      });
      const b2clAgg = sumGstr1TotalsFromB2clLines(gstr1B2clLines);

      const b2clDetailRows = (gstr1B2clLines || []).map((line) => ({
        'Invoice No': line.invoiceNumber || '',
        'Invoice Date': formatDate(line.invoiceDate),
        'Invoice Value': Number(line.invoiceValue || 0),
        'Place Of Supply': line.placeOfSupply || '',
        Rate: Number(line.rate ?? 0),
        'Taxable Value': Number(line.taxableValue || 0),
        IGST: Number(line.igstAmount || 0),
        Cess: Number(line.cessAmount || 0),
        'E-Commerce GSTIN': line.ecommerceGstin || '',
      }));

      const wsB2cl = XLSX.utils.aoa_to_sheet([
        ['Summary of B2CL'],
        [
          'No. of Invoices',
          b2clDistinctInvoices,
          'Total Invoice Value',
          b2clTotalInvoiceValue,
          'Total Taxable Value',
          b2clAgg.taxable,
          'Total IGST',
          b2clAgg.igst,
          'Total Cess',
          b2clAgg.cess,
        ],
        [],
      ]);
      const B2CL_TABLE_ORIGIN_ROW = 4;
      const b2clHeaders = [
        'Invoice No',
        'Invoice Date',
        'Invoice Value',
        'Place Of Supply',
        'Rate',
        'Taxable Value',
        'IGST',
        'Cess',
        'E-Commerce GSTIN',
      ];

      if (b2clDetailRows.length === 0) {
        XLSX.utils.sheet_add_aoa(wsB2cl, [b2clHeaders], { origin: `A${B2CL_TABLE_ORIGIN_ROW}` });
      } else {
        XLSX.utils.sheet_add_json(wsB2cl, b2clDetailRows, {
          origin: `A${B2CL_TABLE_ORIGIN_ROW}`,
        });
      }

      const b2csSourceRows = reportData.rows.filter(
        (r) => !isRegisteredB2bRow(r) && invoiceTotalValue(r) <= B2C_LAKH_THRESHOLD
      );
      const b2csByRate = aggregateB2csTaxableByPlaceAndRate(b2csSourceRows);
      const b2csTotalTaxableAgg = b2csByRate.reduce((a, x) => a + x.taxableValue, 0);
      const b2csTotalCess = b2csByRate.reduce((a, x) => a + x.cessAmount, 0);

      const wsB2cs = XLSX.utils.aoa_to_sheet([
        ['Summary of B2CS'],
        [
          'No. of Invoices',
          b2csSourceRows.length,
          'Total Taxable Value',
          b2csTotalTaxableAgg,
          'Total Cess',
          b2csTotalCess,
        ],
        [],
      ]);
      const B2CS_TABLE_ORIGIN_ROW = 4;

      if (b2csByRate.length === 0) {
        XLSX.utils.sheet_add_aoa(
          wsB2cs,
          [
            [
              'No B2CS rate-wise data (need unregistered customers with invoice ≤ ₹2.5 lakh and taxable GST lines).',
            ],
          ],
          { origin: `A${B2CS_TABLE_ORIGIN_ROW}` }
        );
      } else {
        const b2csExportRows = b2csByRate.map((line) => ({
          Type: 'OE',
          'Place Of Supply': line.placeOfSupply || '',
          Rate: Number(line.rate),
          'Taxable Value': line.taxableValue,
          'Cess Amount': line.cessAmount,
          'E-Commerce GSTIN': '',
        }));
        XLSX.utils.sheet_add_json(wsB2cs, b2csExportRows, {
          origin: `A${B2CS_TABLE_ORIGIN_ROW}`,
        });
        const b2csKeys = [
          'Type',
          'Place Of Supply',
          'Rate',
          'Taxable Value',
          'Cess Amount',
          'E-Commerce GSTIN',
        ];
        const firstBlankB2cs = B2CS_TABLE_ORIGIN_ROW + b2csExportRows.length + 1;
        const wCs = b2csKeys.length;
        const emptyCs = Array(wCs).fill('');
        const totalCs = b2csKeys.map((key) => {
          if (key === 'Type') return 'TOTAL';
          if (key === 'Rate') return '';
          if (key === 'Taxable Value') return b2csTotalTaxableAgg;
          if (key === 'Cess Amount') return b2csTotalCess;
          return '';
        });
        XLSX.utils.sheet_add_aoa(wsB2cs, [emptyCs, emptyCs, totalCs], {
          origin: `A${firstBlankB2cs}`,
        });
      }

      const gstr1CdnrLines = reportData.gstr1CdnrLines || [];
      const cdnrExportRows = gstr1CdnrLines.map((line) => ({
        GSTIN: line.gstin || '',
        'Customer Name': line.customerName || '',
        'Note Number': line.noteNumber || '',
        'Note Date': formatDate(line.noteDate),
        'Note Type': line.noteType || '',
        'Original Invoice No': line.originalInvoiceNo || '',
        'Original Invoice Date': formatDate(line.originalInvoiceDate),
        'Note Value': Number(line.noteValue || 0),
        'Place Of Supply': line.placeOfSupply || '',
        'Reverse Charge': line.reverseCharge || 'N',
        Rate: Number(line.rate ?? 0),
        'Taxable Value': Number(line.taxableValue || 0),
        CGST: Number(line.cgstAmount || 0),
        SGST: Number(line.sgstAmount || 0),
        IGST: Number(line.igstAmount || 0),
        Cess: Number(line.cessAmount || 0),
      }));
      const wsCdnr = XLSX.utils.aoa_to_sheet([['Summary of CDNR'], []]);
      const CDNR_TABLE_ORIGIN_ROW = 3;
      const cdnrHeaders = [
        'GSTIN',
        'Customer Name',
        'Note Number',
        'Note Date',
        'Note Type',
        'Original Invoice No',
        'Original Invoice Date',
        'Note Value',
        'Place Of Supply',
        'Reverse Charge',
        'Rate',
        'Taxable Value',
        'CGST',
        'SGST',
        'IGST',
        'Cess',
      ];
      if (cdnrExportRows.length === 0) {
        XLSX.utils.sheet_add_aoa(wsCdnr, [cdnrHeaders], { origin: `A${CDNR_TABLE_ORIGIN_ROW}` });
      } else {
        XLSX.utils.sheet_add_json(wsCdnr, cdnrExportRows, { origin: `A${CDNR_TABLE_ORIGIN_ROW}` });
      }

      const gstr1CdnurLines = reportData.gstr1CdnurLines || [];
      const cdnurExportRows = gstr1CdnurLines.map((line) => ({
        'Note Number': line.noteNumber || '',
        'Note Date': formatDate(line.noteDate),
        'Note Type': line.noteType || '',
        'Original Invoice No': line.originalInvoiceNo || '',
        'Original Invoice Date': formatDate(line.originalInvoiceDate),
        'Note Value': Number(line.noteValue || 0),
        'Place Of Supply': line.placeOfSupply || '',
        Rate: Number(line.rate ?? 0),
        'Taxable Value': Number(line.taxableValue || 0),
        IGST: Number(line.igstAmount || 0),
        Cess: Number(line.cessAmount || 0),
      }));
      const wsCdnur = XLSX.utils.aoa_to_sheet([['Summary of CDNUR'], []]);
      const CDNUR_TABLE_ORIGIN_ROW = 3;
      const cdnurHeaders = [
        'Note Number',
        'Note Date',
        'Note Type',
        'Original Invoice No',
        'Original Invoice Date',
        'Note Value',
        'Place Of Supply',
        'Rate',
        'Taxable Value',
        'IGST',
        'Cess',
      ];
      if (cdnurExportRows.length === 0) {
        XLSX.utils.sheet_add_aoa(wsCdnur, [cdnurHeaders], { origin: `A${CDNUR_TABLE_ORIGIN_ROW}` });
      } else {
        XLSX.utils.sheet_add_json(wsCdnur, cdnurExportRows, { origin: `A${CDNUR_TABLE_ORIGIN_ROW}` });
      }

      const ex = reportData.gstr1ExempSummary;
      const exRows = (ex && ex.rows) || [];
      const wsExemp = XLSX.utils.aoa_to_sheet([
        ['Summary For Nil rated, exempted and non GST outward supplies'],
        [
          '',
          'Total Nil Rated Supplies',
          'Total Exempted Supplies',
          'Total Non-GST Supplies',
        ],
        [
          '',
          Number(ex?.totalNilRatedSupplies ?? 0),
          Number(ex?.totalExemptedSupplies ?? 0),
          Number(ex?.totalNonGstSupplies ?? 0),
        ],
        [],
        [
          'Description',
          'Nil Rated Supplies',
          'Exempted(other than nil rated)',
          'Non-GST Supplies',
        ],
        ...exRows.map((r) => [
          r.description || '',
          Number(r.nilRatedSupplies ?? 0),
          Number(r.exemptedSupplies ?? 0),
          Number(r.nonGstSupplies ?? 0),
        ]),
      ]);

      const wsHsnB2b = XLSX.utils.aoa_to_sheet(
        buildGstr1HsnSheetAoa('Summary For HSN (B2B)', reportData.gstr1HsnB2bSummary)
      );
      const wsHsnB2c = XLSX.utils.aoa_to_sheet(
        buildGstr1HsnSheetAoa('Summary For HSN (B2C)', reportData.gstr1HsnB2cSummary)
      );

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'GSTR1 Report');
      XLSX.utils.book_append_sheet(wb, wsB2b, 'b2b');
      XLSX.utils.book_append_sheet(wb, wsB2cl, 'b2cl');
      XLSX.utils.book_append_sheet(wb, wsB2cs, 'b2cs');
      XLSX.utils.book_append_sheet(wb, wsCdnr, 'cdnr');
      XLSX.utils.book_append_sheet(wb, wsCdnur, 'cdnur');
      XLSX.utils.book_append_sheet(wb, wsExemp, 'exemp');
      XLSX.utils.book_append_sheet(wb, wsHsnB2b, 'hsn_b2b');
      XLSX.utils.book_append_sheet(wb, wsHsnB2c, 'hsn_b2c');

      const filename = `GSTR1_Report_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter rows by invoice number or customer name (single search field)
  const filteredRows = reportData.rows.filter(row => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const matchesInvoiceNo = row.invoiceNumber && row.invoiceNumber.toLowerCase().includes(query);
    const matchesCustomerName = row.customerName && row.customerName.toLowerCase().includes(query);
    const matchesGst = row.gstNo && String(row.gstNo).toLowerCase().includes(query);
    const matchesPos =
      row.placeOfSupply && String(row.placeOfSupply).toLowerCase().includes(query);
    return matchesInvoiceNo || matchesCustomerName || matchesGst || matchesPos;
  });

  // Paginated rows
  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} to="/reports" color="inherit">
            Reports
          </Link>
          <Typography color="text.primary">GSTR1 Reports</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              {nonGstMode ? 'GSTR1 Reports (Non-GST Bills)' : 'GSTR1 Reports'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={
              isExporting ||
              loading ||
              (reportData.rows.length === 0 && !exempSummaryHasData(reportData.gstr1ExempSummary))
            }
            color={nonGstMode ? 'success' : 'primary'}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        

        {/* Filters */}
        <Card sx={{ mb: 3, border: nonGstMode ? '1px solid' : undefined, borderColor: nonGstMode ? '#00796b' : undefined }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search by Invoice, Customer, GST, or Place of Supply"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Invoice, customer, GST, or place of supply"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Button
                  variant="contained"
                  onClick={fetchReportData}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Report Table — EXEMP (nil / exempt / non-GST) is only in Excel export (exemp sheet), not shown here */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : reportData.rows.length === 0 &&
              !exempSummaryHasData(reportData.gstr1ExempSummary) ? (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                No data available for the selected date range
              </Typography>
            ) : filteredRows.length === 0 && reportData.rows.length > 0 ? (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                No records found matching your search criteria
              </Typography>
            ) : filteredRows.length === 0 && reportData.rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                No invoices with taxable GST lines in this period. Use Export to Excel — the <strong>exemp</strong>{' '}
                sheet has nil / exempt / non-GST summary totals.
              </Typography>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: nonGstMode ? '#006064' : 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invoice No</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>GST No</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Place of Supply</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                          Taxable Amount
                        </TableCell>
                        {/* Dynamic tax columns */}
                        {reportData.taxRates.map((taxRate) => (
                          <TableCell
                            key={taxRate.rate}
                            align="right"
                            sx={{ color: 'white', fontWeight: 'bold' }}
                          >
                            {taxRate.name}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                          Total GST
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                          Total Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            ...(nonGstMode
                              ? { backgroundColor: index % 2 === 0 ? '#f1f8e9' : '#dcedc8' }
                              : { '&:nth-of-type(odd)': { bgcolor: 'action.hover' } })
                          }}
                        >
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell>{row.invoiceNumber}</TableCell>
                          <TableCell>{row.customerName}</TableCell>
                          <TableCell>{row.gstNo || '-'}</TableCell>
                          <TableCell>{row.placeOfSupply || '-'}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.taxableAmount)}
                          </TableCell>
                          {/* Dynamic tax columns */}
                          {reportData.taxRates.map((taxRate) => {
                            const taxName = taxRate.name;
                            const taxAmount = row.taxAmounts && row.taxAmounts[taxName]
                              ? row.taxAmounts[taxName]
                              : 0;
                            return (
                              <TableCell key={taxRate.name} align="right">
                                {taxAmount > 0 ? formatCurrency(taxAmount) : '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            {formatCurrency(row.totalGST)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.totalAmount)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Summary Row */}
                      {reportData.summary && (
                        <TableRow sx={{ bgcolor: nonGstMode ? '#004d40' : 'primary.light', fontWeight: 'bold' }}>
                          <TableCell colSpan={5} sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>TOTAL</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatCurrency(reportData.summary.totalTaxableAmount)}</strong>
                          </TableCell>
                          {/* Dynamic tax columns summary */}
                          {reportData.taxRates.map((taxRate) => {
                            const taxName = taxRate.name;
                            const taxAmount = reportData.summary.totalTaxAmounts && reportData.summary.totalTaxAmounts[taxName]
                              ? reportData.summary.totalTaxAmounts[taxName]
                              : 0;
                            return (
                              <TableCell key={taxRate.name} align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                                <strong>{taxAmount > 0 ? formatCurrency(taxAmount) : '-'}</strong>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatCurrency(reportData.summary.totalGST)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatCurrency(reportData.summary.totalAmount)}</strong>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredRows.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default GSTReport;

